// TUT-491 — as regras puras da caixa de entrada única na tela: os filtros
// Canal / Não lidas / Minhas, as fixadas no topo, os canais oferecidos
// conforme o estado do atendimento, as variáveis das respostas prontas e as
// menções que ainda estão no texto. A união do que a pessoa pode ver é do
// servidor (`GET /conversas`); aqui só se recorta o que chegou.
import type {
  CaixaDeConversas,
  CanalDaConversa,
  ConversaUnificada,
  EstadoDasExternas,
  ParticipanteDaConversa,
} from "./types";

export type RecorteDaCaixa = "todas" | "minhas" | "naoLidas";

export interface FiltrosDaCaixa {
  recorte?: RecorteDaCaixa;
  canais?: readonly CanalDaConversa[];
  busca?: string;
}

/** A ordem e o rótulo dos canais, a mesma em todo lugar. */
export const CANAIS_DA_CAIXA: readonly { canal: CanalDaConversa; rotulo: string }[] = [
  { canal: "whatsapp", rotulo: "WhatsApp" },
  { canal: "email", rotulo: "E-mail" },
  { canal: "outros", rotulo: "Outros canais" },
  { canal: "equipe", rotulo: "Equipe" },
  { canal: "portal", rotulo: "Portal" },
];

export const CANAIS_EXTERNOS: readonly CanalDaConversa[] = ["whatsapp", "email", "outros"];
export const CANAIS_INTERNOS: readonly CanalDaConversa[] = ["equipe", "portal"];

function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("pt-BR");
}

function casaBusca(item: ConversaUnificada, busca: string): boolean {
  const alvo = normalizar(
    [item.titulo, item.previa, item.contato?.email, item.contato?.telefone, ...item.participantes.map((p) => p.nome)]
      .filter(Boolean)
      .join(" "),
  );
  return alvo.includes(normalizar(busca.trim()));
}

function antesDoRecorte(itens: readonly ConversaUnificada[], filtros: FiltrosDaCaixa): ConversaUnificada[] {
  const canais = filtros.canais?.length ? new Set(filtros.canais) : null;
  const busca = filtros.busca?.trim();
  return itens.filter((item) => (!canais || canais.has(item.canal)) && (!busca || casaBusca(item, busca)));
}

function noRecorte(item: ConversaUnificada, recorte: RecorteDaCaixa): boolean {
  if (recorte === "minhas") return item.minha;
  if (recorte === "naoLidas") return item.naoLidas > 0;
  return true;
}

export function filtrarCaixa(itens: readonly ConversaUnificada[], filtros: FiltrosDaCaixa): ConversaUnificada[] {
  const recorte = filtros.recorte ?? "todas";
  return antesDoRecorte(itens, filtros).filter((item) => noRecorte(item, recorte));
}

/** Quantas há em cada recorte, sobre o que o canal e a busca deixaram. */
export function contarRecortes(
  itens: readonly ConversaUnificada[],
  filtros: Omit<FiltrosDaCaixa, "recorte">,
): Record<RecorteDaCaixa, number> {
  const base = antesDoRecorte(itens, filtros);
  return {
    todas: base.length,
    minhas: base.filter((item) => item.minha).length,
    naoLidas: base.filter((item) => item.naoLidas > 0).length,
  };
}

/**
 * TUT-561 — de quem é o avatar de um item da lista: a OUTRA pessoa da
 * conversa direta (a mesma que dá o título: quem é de fora primeiro, senão
 * quem não sou eu). Grupo, a conversa do portal com o escritório e o
 * atendimento não têm uma pessoa só e ficam com o ícone do canal.
 */
export function outraPessoaDaDireta(conversa: ConversaUnificada, meuId: string): ParticipanteDaConversa | null {
  if (conversa.fonte !== "chat" || conversa.tipo !== "DIRECT") return null;
  return (
    conversa.participantes.find((p) => p.externo) ?? conversa.participantes.find((p) => p.userId !== meuId) ?? null
  );
}

/**
 * TUT-561 — a conta cuja foto o avatar pode mostrar: a de quem é da equipe.
 * Quem é de fora entra como `null` e fica nas iniciais; a foto dele nunca é
 * pedida, nem se a lista a trouxesse.
 */
export function contaDaFoto(pessoa: Pick<ParticipanteDaConversa, "userId" | "externo"> | null | undefined): string | null {
  return pessoa && !pessoa.externo ? pessoa.userId : null;
}

export function separarFixadas(itens: readonly ConversaUnificada[]) {
  return {
    fixadas: itens.filter((item) => item.fixada),
    demais: itens.filter((item) => !item.fixada),
  };
}

/**
 * Sem o atendimento pronto, os canais externos nem aparecem como filtro: quem
 * não atende não recebe o selo, e o escritório sem o atendimento ligado não
 * tem o que filtrar. "Outros canais" só quando há conversa de um deles.
 */
export function canaisDisponiveis(
  estado: EstadoDasExternas,
  itens: readonly ConversaUnificada[],
): CanalDaConversa[] {
  if (estado !== "READY") return [...CANAIS_INTERNOS];
  const temOutros = itens.some((item) => item.canal === "outros");
  return CANAIS_DA_CAIXA.map((c) => c.canal).filter((canal) => canal !== "outros" || temOutros);
}

const TRATAMENTOS = /^(?:dra?|eng|sra?|prof)\.?\s+/iu;

/**
 * As variáveis das respostas prontas, preenchidas pela Tutelae. A que não tem
 * valor fica escrita (`{processo}`) e volta em `faltou`, para a tela avisar
 * antes de a pessoa mandar ao cliente um texto com chave crua.
 */
export function preencherResposta(
  texto: string,
  contexto: { nome: string | null; processo: string | null },
): { texto: string; faltou: string[] } {
  const faltou = new Set<string>();
  const primeiroNome = contexto.nome?.split("·")[0]?.trim().replace(TRATAMENTOS, "").split(/\s+/u)[0] ?? "";
  const valores: Record<string, string | null> = {
    primeiro_nome: primeiroNome || null,
    processo: contexto.processo,
  };
  const preenchido = texto.replace(/\{(\w+)\}/gu, (chave, nome: string) => {
    const valor = valores[nome];
    if (valor) return valor;
    faltou.add(nome);
    return chave;
  });
  return { texto: preenchido, faltou: [...faltou] };
}

/** Só vai a menção cujo `@Nome` ainda está no texto: apagar o nome desfaz a menção. */
export function mencoesQueFicaram(texto: string, escolhidas: readonly { userId: string; nome: string }[]): string[] {
  return [...new Set(escolhidas.filter((m) => texto.includes(`@${m.nome}`)).map((m) => m.userId))];
}

function instante(item: Pick<ConversaUnificada, "ultimaAtividadeEm">): number {
  return item.ultimaAtividadeEm ? Date.parse(item.ultimaAtividadeEm) : Number.NEGATIVE_INFINITY;
}

/**
 * A regra da lista, a MESMA do servidor (`ordenarCaixa` em
 * `apps/api/src/domain/conversas.ts`): as fixadas no topo, na ordem de
 * fixação; o resto, da mais recente para a mais antiga. Um helper só na web:
 * o "Carregar mais" e o modo demonstração usam este.
 */
export function ordenarComFixadas<T extends Pick<ConversaUnificada, "id" | "ultimaAtividadeEm">>(
  itens: readonly T[],
  fixadas: readonly string[],
): (T & { fixada: boolean })[] {
  const posicao = new Map(fixadas.map((id, indice) => [id, indice]));
  return itens
    .map((conversa) => ({ ...conversa, fixada: posicao.has(conversa.id) }))
    .sort((a, b) => {
      const pa = posicao.get(a.id);
      const pb = posicao.get(b.id);
      if (pa !== undefined && pb !== undefined) return pa - pb;
      if (pa !== undefined) return -1;
      if (pb !== undefined) return 1;
      return instante(b) - instante(a);
    });
}

/**
 * O "Carregar mais" pagina só o atendimento: cada página traz o chat inteiro
 * de novo (e as fixadas) e mais 25 conversas externas. Junta sem repetir (a
 * página mais nova vence) e reordena pela regra do servidor. As fixadas, as
 * indisponíveis e o estado da fonte vêm da PRIMEIRA página, que é a que o
 * tempo real recarrega; a paginação (`pagina`, `temMais`) vem da última.
 */
export function juntarPaginas(paginas: readonly CaixaDeConversas[]): CaixaDeConversas | null {
  const primeira = paginas[0];
  const ultima = paginas.at(-1);
  if (!primeira || !ultima) return null;
  const porId = new Map<string, ConversaUnificada>();
  for (const pagina of paginas) for (const conversa of pagina.itens) porId.set(conversa.id, conversa);
  // A primeira página é a fresca: o que ela traz vence o das outras.
  for (const conversa of primeira.itens) porId.set(conversa.id, conversa);
  return {
    ...primeira,
    itens: ordenarComFixadas([...porId.values()], primeira.fixadas),
    // Re-revisão (M-C) — a fixada que uma página seguinte trouxe deixa de ser
    // "indisponível": sem isso ela aparecia duas vezes, com a chave repetida.
    fixadasIndisponiveis: primeira.fixadasIndisponiveis.filter((id) => !porId.has(id)),
    externas: { ...primeira.externas, pagina: ultima.externas.pagina, temMais: ultima.externas.temMais },
  };
}
